import { versionString } from '../../contexts/version';

const CMS_LOCALE_BY_APP_LOCALE = {
  en: 'en',
  'zh-Hant-HK': 'zh-Hant-HK',
  zh: 'zh-CN'
};

const LATEST_RELEASE_COUNT = 3;

// Must match the encoding in headless-cms/src/api/feature-update/content-types/feature-update/lifecycles.js
export const toVersionNumber = version => {
  const [major, minor = 0, patch = 0] = String(version).split('.').map(Number);
  return major * 10000 + minor * 100 + patch;
};

export const formatVersionNumber = n => `${Math.floor(n / 10000)}.${Math.floor(n / 100) % 100}.${n % 100}`;

export const getCmsLocale = locale => CMS_LOCALE_BY_APP_LOCALE[locale] || 'en';

export const buildFeatureUpdateUrl = (cmsUrl, cmsLocale, maxVersion, isClient) =>
  `${cmsUrl.replace(/\/$/, '')}/api/feature-updates?locale=${encodeURIComponent(
    cmsLocale
  )}&filters[version][$lte]=${maxVersion}&filters[isClient][$eq]=${isClient}&sort=version:desc&pagination[limit]=${LATEST_RELEASE_COUNT}&populate[features][populate][cover]=*`;

const getAttributes = value => value?.attributes || value || {};

const getMediaAttributes = cover => {
  const mediaData = Array.isArray(cover?.data) ? cover.data[0] : cover?.data || cover;
  return getAttributes(mediaData);
};

const resolveMediaUrl = (cmsUrl, url) => {
  if (!url || typeof url !== 'string') return null;
  try {
    return new URL(url, cmsUrl).toString();
  } catch (error) {
    return null;
  }
};

const normalizeMedia = (cover, cmsUrl) => {
  const attributes = getMediaAttributes(cover);
  const mime = typeof attributes.mime === 'string' ? attributes.mime : '';
  const kind = mime.startsWith('video/') ? 'video' : mime.startsWith('image/') ? 'image' : null;
  if (!kind) return null;

  const imageFormats = attributes.formats || {};
  const imageUrl =
    kind === 'image'
      ? imageFormats.large?.url ||
        imageFormats.medium?.url ||
        imageFormats.small?.url ||
        imageFormats.thumbnail?.url ||
        attributes.url
      : attributes.url;
  const url = resolveMediaUrl(cmsUrl, imageUrl);
  if (!url) return null;

  return {
    kind,
    url,
    mime,
    alt: attributes.alternativeText || attributes.name || ''
  };
};

const normalizeFeature = (value, cmsUrl) => {
  const feature = getAttributes(value);
  const media = normalizeMedia(feature.cover, cmsUrl);
  if (!feature.id || !feature.title || !feature.description || !feature.path || !media) return null;

  return {
    id: feature.id,
    title: feature.title,
    description: feature.description,
    path: feature.path,
    media
  };
};

const sortByVersion = entries =>
  entries
    .filter(entry => getAttributes(entry).publishedAt)
    .sort((left, right) => (getAttributes(right).version || 0) - (getAttributes(left).version || 0));

const normalizeEntry = (entry, cmsUrl, locale) => {
  const attributes = getAttributes(entry);
  const features = Array.isArray(attributes.features)
    ? attributes.features.map(feature => normalizeFeature(feature, cmsUrl)).filter(Boolean)
    : [];
  if (!Number.isInteger(attributes.version) || !features.length) return null;

  return {
    version: attributes.version,
    publishedAt: attributes.publishedAt,
    locale,
    features
  };
};

export const normalizeFeatureUpdates = (payload, { cmsUrl, locale }) => {
  const entries = Array.isArray(payload?.data) ? sortByVersion(payload.data) : [];
  return entries
    .map(entry => normalizeEntry(entry, cmsUrl, locale))
    .filter(Boolean)
    .slice(0, LATEST_RELEASE_COUNT);
};

export const normalizeFeatureUpdate = (payload, options) => {
  const releases = normalizeFeatureUpdates(payload, options);
  if (!releases.length) return null;
  return { ...releases[0], releases };
};

const requestFeatureUpdate = async (fetchImpl, cmsUrl, cmsLocale, maxVersion, isClient) => {
  const response = await fetchImpl(buildFeatureUpdateUrl(cmsUrl, cmsLocale, maxVersion, isClient));
  if (response?.ok === false) {
    throw new Error(`Feature update request failed with ${response.status || 'an HTTP error'}`);
  }
  return normalizeFeatureUpdate(await response.json(), { cmsUrl, locale: cmsLocale });
};

export const fetchLatestFeatureUpdate = async (
  { cmsUrl = process.env.HEADLESS_CMS_URL, locale = 'en', appVersion = versionString, isClient },
  fetchImpl = fetch
) => {
  if (!cmsUrl) return null;

  const cmsLocale = getCmsLocale(locale);
  const maxVersion = toVersionNumber(appVersion);
  const localizedUpdate = await requestFeatureUpdate(fetchImpl, cmsUrl, cmsLocale, maxVersion, isClient);
  if (localizedUpdate || cmsLocale === 'en') return localizedUpdate;

  return requestFeatureUpdate(fetchImpl, cmsUrl, 'en', maxVersion, isClient);
};
